// Minimal Vite env surface. This lives in the module that reads it rather than in
// global.d.ts because consumers pull this file into their own tsconfig program
// transitively (e.g. external/apps/www via @web-monorepo/mds) and never pick up this
// package's ambient declaration files, so `import.meta.env` below would not type-check
// there. Non-optional to keep the exact `import.meta.env.X` member-access form that
// Vite's define replacement matches; runtime absence (plain Node ESM) is handled with a
// `typeof import.meta.env` guard at the call site.
declare global {
  interface ImportMeta {
    env: Record<string, string | boolean | undefined>;
  }
}

export { ref, snapshot, getVersion, type Snapshot } from "valtio";
// eslint-disable-next-line @web-monorepo/no-direct-valtio-imports
import {
  proxy as innerProxy,
  subscribe as innerSubscribe,
  unstable_getInternalStates as getInternalStates,
} from "valtio";

// Access the WeakSet that ref() uses to mark non-proxied objects. This lets
// captureInitial/cloneForRestore detect ref()-wrapped plain objects, which
// isPlainObject() would otherwise misidentify as deep-cloneable.
const { refSet } = getInternalStates();
// eslint-disable-next-line @web-monorepo/no-direct-valtio-imports -- this file is the canonical re-export; the lint rule blocks direct valtio imports everywhere else
import { proxySet as innerProxySet, proxyMap as innerProxyMap } from "valtio/utils";

// change the default behavior of subscribe to be synchronous. This means that if you
// have a call like this:
// useEffect(() => subscribe(store,callback), [store,callback])
// you are actually guaranteed to receive changes, whereas before, you are not -
// if a change happens in a render cycle where callback gets swapped out,
// you can lose callbacks.
export const subscribe: typeof innerSubscribe = (proxy, callback, notifyInSync) =>
  innerSubscribe(proxy, callback, notifyInSync ?? true);

// ---------------------------------------------------------------------------
// Test-only store registry + auto-reset
// ---------------------------------------------------------------------------
//
// When enableStoreResetTracking() is called from a test setup file, every
// proxy()/proxySet()/proxyMap() call registers its initial state in the
// registry. resetAllStores() then restores every still-live store to that
// initial snapshot before each test, eliminating the need for hand-written
// _resetXForTests() helpers.
//
// Production impact: zero. The `tracking` flag is dead-code-eliminated by the
// bundler (process.env.NODE_ENV is statically replaced). When false the wrappers
// below are a single boolean check away from the raw innerProxy call.
//
// useClassState is deliberately opted out (via proxyWithoutTracking) because its
// stores are component-scoped (held in useRef) and never leak between tests.
//
// Tracking is ON by default in every non-production environment (see `tracking`
// declaration below for the full rationale).

type RegistryEntry =
  | { ref: WeakRef<object>; initial: unknown; kind: "object" }
  | { ref: WeakRef<Set<unknown>>; initial: unknown[]; kind: "set" }
  | {
      ref: WeakRef<Map<unknown, unknown>>;
      initial: [unknown, unknown][];
      kind: "map";
    };

const registry = new Set<RegistryEntry>();

// Tracking is ON by default in every non-production environment.
//
// Why? ES module `import` statements are hoisted and evaluated before any
// module-level code runs in the importing file. That means a setup file like
// `vitest.setup.ts` cannot call `enableStoreResetTracking()` *before* an
// imported helper (e.g. `#/test/library`) triggers the evaluation of
// `runtime.tsx`, which creates `dialogStore = proxy({...})`. If tracking were
// off at that point, the store would never be registered.
//
// Making tracking always-on in non-production solves this: every proxy() call
// that happens at import time is automatically captured with its initial state,
// so `resetAllStores()` can restore them in `beforeEach`.
//
// STORYBOOK_TEST covers the static-build test-runner path (storybook-test-shard):
// `storybook build` runs with NODE_ENV=production, so without this flag tracking
// never engages there — resetAllStores() silently no-ops and module-level stores
// (e.g. the openDialog() stack) leak state between stories. Storybook's vite
// builder statically replaces `import.meta.env.STORYBOOK_TEST` in test builds;
// app production builds leave it undefined. The typeof guard keeps this module
// loadable in non-Vite runtimes (plain Node ESM) where import.meta.env is absent.
//
// Production impact: zero — tracking stays false in app builds.
//
// istanbul ignore next -- @preserve: the right-hand operand only evaluates when
// NODE_ENV is "production", which never happens under vitest (NODE_ENV=test),
// so the STORYBOOK_TEST branch is unreachable in coverage runs by construction.
let tracking =
  process.env.NODE_ENV !== "production" ||
  (typeof import.meta.env !== "undefined" && Boolean(import.meta.env.STORYBOOK_TEST));

/** No-op in test/development (tracking is already on). Retained for clarity
 *  in setup files and for parity with disableStoreResetTracking(). */
export function enableStoreResetTracking() {
  // NODE_ENV is statically replaced by the bundler; the production guard is
  // dead-code-eliminated in production builds and can't be reached in tests.
  // istanbul ignore if -- @preserve
  if (process.env.NODE_ENV === "production") return;
  tracking = true;
}

/** Reset all registered live stores to their captured initial state. */
export function resetAllStores() {
  for (const entry of registry) {
    const live = entry.ref.deref();
    // GC-dependent path: WeakRef.deref() returning undefined requires the proxy to have
    // been garbage-collected, which isn't deterministically testable.
    // This cleanup prevents unbounded registry growth in long test suites.
    // istanbul ignore if -- @preserve
    if (!live) {
      registry.delete(entry);
      continue;
    }
    if (entry.kind === "set") {
      const s = live as Set<unknown>;
      s.clear();
      for (const v of entry.initial) s.add(cloneForRestore(v));
    } else if (entry.kind === "map") {
      const m = live as Map<unknown, unknown>;
      m.clear();
      for (const [k, v] of entry.initial) {
        m.set(cloneForRestore(k), cloneForRestore(v));
      }
    } else {
      restoreObject(live as Record<string, unknown>, entry.initial as Record<string, unknown>);
    }
  }
}

/** Reset and disable tracking (useful between test suites if needed). */
export function disableStoreResetTracking() {
  tracking = false;
  registry.clear();
}

// Internal helper used by useClassState so its component-scoped stores skip tracking.
// Not a public export — only imported by useClassState.ts. Callers outside this package
// must use the public proxy().
export const proxyWithoutTracking: typeof innerProxy = innerProxy;

// ---------------------------------------------------------------------------
// Wrapped proxy / proxySet / proxyMap
// ---------------------------------------------------------------------------

// Re-export proxy with tracking. The outer cast to typeof innerProxy preserves Valtio's
// generic type signature (<T extends object>(baseObject?: T) => T) while the inner
// implementation works with the broader `object` type.
export const proxy = ((init: object) => {
  const p = innerProxy(init as Parameters<typeof innerProxy>[0]);
  if (tracking) {
    registry.add({
      ref: new WeakRef(p),
      initial: captureInitial(init),
      kind: "object",
    });
  }
  return p;
}) as typeof innerProxy;

export const proxySet: typeof import("valtio/utils").proxySet = <T>(initialValues?: Iterable<T> | null) => {
  const s = innerProxySet<T>(initialValues);
  if (tracking) {
    const captured: T[] = initialValues ? Array.from(initialValues).map((v) => captureInitial(v) as T) : [];
    registry.add({
      ref: new WeakRef(s) as WeakRef<Set<unknown>>,
      initial: captured,
      kind: "set",
    });
  }
  return s;
};

// The outer cast to typeof innerProxyMap preserves Valtio's generic signature.
// The inner cast on entries works around the readonly-tuple vs mutable-tuple mismatch
// between our wrapper's parameter type and innerProxyMap's Iterable<[K,V]> expectation.
export const proxyMap = (<K, V>(entries?: readonly (readonly [K, V])[] | null) => {
  const m = innerProxyMap<K, V>(entries as Iterable<[K, V]> | null | undefined);
  if (tracking) {
    const captured: [unknown, unknown][] = entries
      ? entries.map(([k, v]) => [captureInitial(k), captureInitial(v)])
      : [];
    registry.add({
      ref: new WeakRef(m) as WeakRef<Map<unknown, unknown>>,
      initial: captured,
      kind: "map",
    });
  }
  return m;
}) as typeof innerProxyMap;

// ---------------------------------------------------------------------------
// captureInitial — deep clone mirroring Valtio's canProxy rule
// ---------------------------------------------------------------------------
//
// Rules (mirrors valtio/src/vanilla.ts canProxyDefault):
//  - primitives → as-is
//  - functions  → by reference (methods stay live; never cloned)
//  - non-proxiable objects (ref()-wrapped, class instances, Date, RegExp, etc.) → by reference
//  - plain arrays → map elements through captureInitial
//  - plain objects → copy own enumerable keys, recursing
//
// ref()-wrapped values are detected via valtio's internal refSet WeakSet (the only
// way to check — ref() leaves no marker on the object itself). They are captured
// by reference so their identity is preserved on reset. Class instances are also
// kept by reference — which is why Bucket-1 companion Sets must become plain object
// fields rather than ref(Set): a ref's contents would never be deep-restored.

function isPlainObject(x: unknown): x is Record<string, unknown> {
  if (x === null || typeof x !== "object") return false;
  const proto: unknown = Object.getPrototypeOf(x);
  return proto === Object.prototype || proto === null;
}

function captureInitial(x: unknown): unknown {
  if (x === null || x === undefined) return x;
  if (typeof x !== "object" && typeof x !== "function") return x; // primitives
  if (typeof x === "function") return x; // functions by reference
  if (Array.isArray(x)) return x.map(captureInitial);
  if (refSet.has(x)) return x; // ref()-wrapped → by reference
  if (!isPlainObject(x)) return x; // class instances, Date, RegExp → by ref
  const result: Record<string, unknown> = {};
  for (const key of Object.keys(x)) {
    result[key] = captureInitial(x[key]);
  }
  return result;
}

// ---------------------------------------------------------------------------
// restoreObject — reconcile a live proxy back to its captured initial state
// ---------------------------------------------------------------------------
//
// 1. Delete keys present on the live store but absent from initial (removes
//    test-added keys, e.g. optional fields the test set).
// 2. For each initial key, write a fresh clone so consecutive resets don't
//    share mutable substructure.
// 3. For nested plain objects, recurse to preserve sub-proxy identity.
// 4. Functions and non-plain objects are written by reference (no clone).

function cloneForRestore(x: unknown): unknown {
  if (x === null || x === undefined) return x;
  if (typeof x !== "object" && typeof x !== "function") return x;
  if (typeof x === "function") return x;
  if (Array.isArray(x)) return x.map(cloneForRestore);
  if (refSet.has(x)) return x; // ref()-wrapped → by reference
  if (!isPlainObject(x)) return x;
  const result: Record<string, unknown> = {};
  for (const key of Object.keys(x)) {
    result[key] = cloneForRestore(x[key]);
  }
  return result;
}

function restoreObject(target: Record<string, unknown>, initial: Record<string, unknown>) {
  // Remove keys the test added
  for (const key of Object.keys(target)) {
    if (!(key in initial)) {
      delete target[key];
    }
  }
  // Restore initial keys
  for (const key of Object.keys(initial)) {
    const initVal = initial[key];
    const liveVal = target[key];
    // Recurse into nested plain objects to preserve sub-proxy identity.
    // Skip recursion if initVal was ref()-wrapped — write it back by reference instead.
    if (isPlainObject(initVal) && isPlainObject(liveVal) && !refSet.has(initVal)) {
      restoreObject(liveVal, initVal);
    } else {
      target[key] = cloneForRestore(initVal);
    }
  }
}
